import { useMutation, useQuery } from '@tanstack/react-query';
import {
  checkGa4connection,
  checkUserGa4PropertyId,
  fetchGa4Info,
  fetchGa4Url,
  updateGa4PropertyId,
  updateUserGa4PropertyId,
} from 'api/ga4';
import { useState } from 'react';
import { useCampaign } from './useCampaign';
import { useLocation, matchPath } from 'react-router-dom';
import { RouteList } from 'types';
import { queryKeys } from 'utils';
import invariant from 'tiny-invariant';
import { useUsers } from './useUsers';

export const useGa4 = () => {
  const { campaignId, getCampaignQuery } = useCampaign();
  const [ga4PropertyId, setGa4PropertyId] = useState('');
  const [failureCount, setFailureCount] = useState(0);
  const { userProfileQuery } = useUsers();
  const [isConnectionSuccess, setIsConnectionSuccess] = useState<boolean | null>(null);
  const [isUserConnectionSuccess, setIsUserConnectionSuccess] = useState<boolean | null>(null);
  const { pathname, search } = useLocation();

  const isConfigPage = !!matchPath({ path: RouteList.Ga4Config }, pathname);

  const getGa4CampaignUrlQuery = useQuery({
    queryKey: queryKeys.ga4Url.byCampaignId(campaignId!),
    queryFn: () => fetchGa4Url(campaignId!),
    enabled: !!campaignId && !isConfigPage,
  });

  const campaignUrl = getGa4CampaignUrlQuery.data?.url;
  let secretToken = '';

  if (isConfigPage) {
    secretToken = new URLSearchParams(search).get('secret') || '';
  } else if (campaignUrl) {
    secretToken = new URLSearchParams(new URL(campaignUrl).search).get('secret') || '';
  }

  const getGa4InfoQuery = useQuery({
    queryKey: queryKeys.ga4Info.byCampaignId(campaignId!),
    queryFn: () => fetchGa4Info(campaignId!, secretToken),
    enabled: !!campaignId && !!secretToken,
  });

  const updateGa4PropertyIdMutation = useMutation({
    mutationFn: () => {
      invariant(campaignId);
      return updateGa4PropertyId(campaignId, ga4PropertyId, secretToken);
    },
    onSuccess: getGa4InfoQuery.refetch,
  });

  const updateUserGa4PropertyIdMutation = useMutation({
    mutationFn: (ga4PropertyId: string) => {
      return updateUserGa4PropertyId(ga4PropertyId);
    },
    onSuccess: userProfileQuery.refetch,
  });

  const checkUserGa4ConnectionMutation = useMutation({
    mutationFn: (ga4PropertyId: string) => {
      return checkUserGa4PropertyId(ga4PropertyId);
    },
    onSuccess: ({ successful }) => {
      setIsUserConnectionSuccess(successful);
    },
  });

  const checkGa4ConnectionMutation = useMutation({
    mutationFn: ({ sendSuccessEmail }: { sendSuccessEmail: boolean; shouldCount?: boolean }) => {
      invariant(campaignId);
      return checkGa4connection(campaignId, sendSuccessEmail);
    },
    onSuccess: ({ successful }, { shouldCount }) => {
      setIsConnectionSuccess(successful);
      setFailureCount((prev) => (successful || !shouldCount ? 0 : ++prev));
      if (!isConfigPage) getCampaignQuery.refetch();
    },
  });

  return {
    getGa4InfoQuery,
    getGa4CampaignUrlQuery,
    updateGa4PropertyIdMutation,
    updateUserGa4PropertyIdMutation,
    ga4PropertyId,
    setGa4PropertyId,
    failureCount,
    isConnectionSuccess,
    isUserConnectionSuccess,
    setIsConnectionSuccess,
    checkGa4ConnectionMutation,
    checkUserGa4ConnectionMutation,
  };
};
